import {
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatMenuModule } from "@angular/material/menu";
import { SharedModule } from "../../shared.module";
import { ActivatedRoute, Router } from "@angular/router";
import { SpinService } from "../../services/spin-service.service";
import { ChipsComponent } from "../chips/chips.component";
import { asapScheduler } from "rxjs";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { InputSearchComponent } from "../input-search/input-search.component";

@Component({
  selector: "vl-multi-checkbox",
  standalone: true,
  imports: [
    MatCheckbox,
    SharedModule,
    ChipsComponent,
    NgClass,
    NgTemplateOutlet,
    MatMenuModule,
    InputSearchComponent,
  ],
  templateUrl: "./multi-checkbox.component.html",
  styleUrl: "./multi-checkbox.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiCheckboxComponent),
      multi: true,
    },
  ],
})
export class MultiCheckboxComponent<T> implements ControlValueAccessor {
  elements = input<{ name: string; value: T }[]>();
  text = input<string>();
  icon = input<string>("expand_more");
  routeParam = input<string>(null);
  label = input<string>();
  showNumber = input<boolean>(false);
  showChips = input<boolean>(false);
  maxChips = input<number>(2);
  value = model<T[]>([]);
  isEqualPredicate = input<(a: T, b: T) => boolean>((a, b) => {
    if (
      a &&
      b &&
      typeof a === "object" &&
      typeof b === "object" &&
      "id" in a &&
      "id" in b
    ) {
      return a.id === b.id;
    }
    return a === b;
  });
  disabled = signal(false);
  emptyLabel = input<string>();
  showTextFilter = input<boolean>(false);
  chipsSeparator = input<TemplateRef<any>>();

  filterText = signal<string>("");

  onSelect = output<T>();

  selectedElements = computed(() => {
    if (!this.showTextFilter()) {
      return [];
    }
    return (
      this.value()
        ?.map((v) =>
          this.elements()?.find((e) => this.isEqualPredicate()(v, e.value)),
        )
        .filter(Boolean) ?? []
    );
  });

  filteredElements = computed(() => {
    const elements = this.elements();
    const filter = this.filterText()?.toLowerCase().trim();
    const selectedValues = this.value() ?? [];

    let filtered = elements;

    if (filter) {
      filtered = elements?.filter((e) =>
        filter.split(" ").every((word) => e.name.toLowerCase().includes(word)),
      );
    }

    if (this.showTextFilter()) {
      filtered = filtered?.filter(
        (e) => !selectedValues.some((v) => this.isEqualPredicate()(v, e.value)),
      );
    }

    return filtered;
  });

  valueElements = computed(() => {
    return this.value()
      ?.map((v) =>
        this.elements().find((e) => this.isEqualPredicate()(v, e.value)),
      )
      .filter(Boolean);
  });
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  spinService = inject(SpinService);

  buttonState = computed(() => (this.value()?.length ? "active" : "enabled"));

  onChangeFn = (v) => {};
  onTouchFn = (v) => {};

  ngAfterViewInit() {
    if (this.routeParam()) {
      this.spinService.disableCanceler();
    }
  }

  constructor() {
    effect(
      () => {
        if (this.elements()?.length === 0) {
          return;
        }
        const currentValues = untracked(() => this.value()) ?? [];
        const previousSelected = new Set(currentValues);
        if (this.routeParam()) {
          this.activatedRoute.snapshot.queryParams[this.routeParam()]
            ?.split(",")
            .filter(Boolean)
            .forEach((elem) => {
              previousSelected.add(elem);
            });
        }
        if (this.elements()?.length) {
          const val = [...previousSelected]
            .map((v) =>
              this.elements().find((e) => this.isEqualPredicate()(e.value, v)),
            )
            .filter(Boolean);
          const newValues = val.map((v) => v.value);
          // Skip emit if reconciled value is identical — avoids infinite loops
          // when elements change but the selection stays the same
          const isEqual = this.isEqualPredicate();
          if (
            newValues.length === currentValues.length &&
            newValues.every((nv, i) => isEqual(nv, currentValues[i]))
          ) {
            return;
          }
          this.emitChanges(newValues);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy() {
    if (this.routeParam()) {
      this.spinService.endDisableCanceler();
    }
  }

  emitChanges(selectedElements: T[]) {
    this.onChangeFn(selectedElements);
    asapScheduler.schedule(() => {
      this.value.set(selectedElements);
    });
    if (this.routeParam()) {
      const queryParams = {
        [this.routeParam()]: selectedElements.join(","),
      };
      this.router.navigate([], {
        replaceUrl: true,
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: "merge",
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }

  writeValue(obj: T[]): void {
    asapScheduler.schedule(() => {
      this.value.set(obj);
    });
  }

  selectElement(e: MouseEvent, element: { name: string; value: T }) {
    e.stopPropagation();
    let selectedElements = [];
    if (this.value()?.find((v) => this.isEqualPredicate()(v, element.value))) {
      selectedElements = this.value().filter(
        (v) => !this.isEqualPredicate()(v, element.value),
      );
    } else {
      this.onSelect.emit(element.value);
      if (this.value()) {
        selectedElements = [...this.value()];
      }
      selectedElements.push(element.value);
    }
    this.emitChanges(selectedElements);
  }

  removeElement(elem: { name: string; value: T }) {
    const selectedElements = this.value().filter(
      (v) => !this.isEqualPredicate()(v, elem.value),
    );
    this.emitChanges(selectedElements);
  }

  removeAll() {
    this.emitChanges([]);
  }

  isChecked(element: { name: string; value: T }) {
    return !!this.value()?.find((e) =>
      this.isEqualPredicate()(e, element.value),
    );
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled.set(isDisabled);
  }

  onMenuOpened() {
    if (this.showTextFilter()) {
      setTimeout(() => {
        const input = document.querySelector(
          ".multi-checkbox-menu input",
        ) as HTMLInputElement;
        input?.focus();
      }, 50);
    }
  }
}
